import React, { useCallback, useContext, useEffect, useState } from 'react';
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import moment from 'moment';
import CircularProgress from '@material-ui/core/CircularProgress';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import GetAppIcon from '@material-ui/icons/GetApp';

import InvestmentProposalContext from '../../../containers/InvestmentProposalContainer/context';
import discretionaryServiceInterface from '../../../utils/discretionaryServiceInterface';
import OnboardingHub from './journey/OnboardingHub';
import ModelChangeHub from './journey/ModelChangeHub';
import AccountChangeHub from './journey/AccountChangeHub';
import './InvestmentProposalHub.scss';
import { getMonthlyFee } from '../HubUtils';

const DialogTitle = (props) => {
    const { children, classes, onClose } = props;
    return (
        <MuiDialogTitle disableTypography classes={classes}>
            <h6>{children}</h6>
            {onClose ? (
                <IconButton aria-label="close" className='poaCloseButton' onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
};

const InvestmentProposalHub = props => {
    const { status, applicationConfig, urlParam } = props;
    const { state, dispatch } = useContext(InvestmentProposalContext);
    const {
        agreementsGroupByType,
        externalAccounts,
        modelAssetAllocationAssociatedFund,
        previousModelAssetAllocationAssociatedFund,
        clientDetails,
        yearlyFee,
        dedicatedTotal,
        asOfDate,
        displayAgentModal,
        proposalId
    } = state;
    const [hasAccounts, setHasAccounts] = useState(false);
    const [hasRAS, setHasRAS] = useState(false);
    const [showPOAModal, setShowPOAModal] = useState(false);
    const isInternal = window.location.hostname.indexOf('.awstrp.net') !== -1;
    const hasPOA = agreementsGroupByType.some(agreement => agreement.powerOfAttorney && agreement.powerOfAttorney.length);
    const poaStorage = window.sessionStorage;

    useEffect(() => {
        if (status && status.client) {
            if (status.client === 'PRIMARY') {
                setHasAccounts(status.clientHasAccounts);
                setHasRAS(status.clientHasRAS);
            } else {
                setHasAccounts(status.coClientHasAccounts);
                setHasRAS(status.coClientHasRAS);
            }
        }
    }, [status]);

    useEffect(() => {
        if (displayAgentModal && hasPOA && (!poaStorage.proposalId || poaStorage.proposalId !== `${proposalId}`)) {
            setShowPOAModal(true);
        }
    }, [displayAgentModal, hasPOA, proposalId]);

    const dismissPOA = () => {
        poaStorage.setItem('proposalId', proposalId);
        setShowPOAModal(false);
    };

    const downloadPOA = () => {
        window.open('https://www.troweprice.com/content/dam/iinvestor/Forms/POA.pdf', '_blank')
        dismissPOA();
    };

    const assetsAsOfDate = asOfDate ? moment(asOfDate).format('l') : agreementsGroupByType.reduce((acc, curr) => {
        acc.push(curr.agreements)
        return acc
    }, [])
        .reduce((ac, curr) => {
            ac.push(curr.map(agreement => agreement.holdings));
            return ac.flat(2);
        }, [])
        .reduce((acc, curr) => {
            const currentDate = curr !== undefined && curr.asOfDate ? moment(curr.asOfDate) : acc;
            return currentDate.isBefore(acc) ? moment(curr.asOfDate) : acc;
        }, moment().subtract(1, 'days'))
        .format('l');

    const fetchInvestmentProposalData = useCallback(async () => {
        try {
            const response = await discretionaryServiceInterface.getInvestmentProposalAllClient(applicationConfig, urlParam);
            dispatch({
                type: "UPDATE_INVESTMENT_PROPOSAL",
                payload: response.data,
            });
        } catch (err) {
            // redirect to error page
            // eslint-disable-next-line no-console
            console.log("Unable to fetch data from GET investment proposal endpoint.")
        }
    }, [dispatch, applicationConfig, urlParam]);

    useEffect(() => {
        fetchInvestmentProposalData();
    }, [fetchInvestmentProposalData]);

    const saveScreenCapture = useCallback(() => {
        const captureContainer = document.querySelector("#captureProposal");
        const svgTags = captureContainer.querySelectorAll("svg");
        // set needed attributes on SVGs to be captured successfully
        svgTags.forEach(item => {
            item.setAttribute("width", item.getBoundingClientRect().width);
            item.setAttribute("height", item.getBoundingClientRect().height);
            // eslint-disable-next-line no-param-reassign
            item.style.width = null;
            // eslint-disable-next-line no-param-reassign
            item.style.color = item.getAttribute("color");
            // eslint-disable-next-line no-param-reassign
            item.style.fill = item.getAttribute("color");
        });
        html2canvas(captureContainer, {
            windowWidth: 1262,
            width: 1262,
            removeContainer: false,
        }).then(canvas => {
            const snapshot = canvas.toDataURL('image/jpeg', 0.5);
            // create pdf file to hold snapshot and configure page height and width
            // eslint-disable-next-line new-cap
            const pdf = new jsPDF();
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const pdfHeight = (canvas.height * pageWidth) / canvas.width;
            let position = 0;
            // add initial pdf page
            pdf.addImage(snapshot, "jpeg", 1, 0, pageWidth, pdfHeight);
            let remainingHeight = pdfHeight - pageHeight;
            // add any additional pdf pages
            while (remainingHeight >= 0) {
                position -= pageHeight;
                pdf.addPage();
                pdf.addImage(snapshot, "jpeg", 1, position, pageWidth, pdfHeight);
                remainingHeight -= pageHeight;
            }
            // create blob with pdf and set as formData
            const blobPDF = new Blob([pdf.output('blob')], { type: 'application/pdf' })
            const formDataPdf = new FormData();
            formDataPdf.append('proposal', blobPDF);
            // use this to test issues in stage via online base64 to pdf converter
            // const reader = new FileReader();
            // reader.onload = () => {
            //     const b64 = reader.result.replace(/^data:.+;base64,/, '');
            //     // eslint-disable-next-line no-console
            //     console.log(b64);
            // };
            // reader.readAsDataURL(blobPDF);
            // as an alternate to the above for testing in stage without pdf saves, use the next line in local sandbox
            // pdf.save("test.pdf");
            // send pdf for retention
            discretionaryServiceInterface.saveInvestmentProposalScreenCapture(applicationConfig, formDataPdf);
        });
    }, [applicationConfig]);

    useEffect(() => {
        if ((state.clientDetails.length > 0 && (window.location.hostname.indexOf('.awstrp.net') === -1 || window.location.hostname.indexOf('localhost:3000') !== -1))) {
            setTimeout(() => saveScreenCapture(), 2000);
        }
    }, [state, saveScreenCapture]);

    const Hub = () => {
        const primary = status && status.client === 'PRIMARY';
        const currentUser = primary ? 'client' : 'coClient';
        const partner = primary ? 'coClient' : 'client';
        const monthlyFee = getMonthlyFee(yearlyFee);
        const hasNewNonRasAgreements = agreementsGroupByType && agreementsGroupByType.some(agreementGroup => agreementGroup.agreements && agreementGroup.agreements.length);
        const hasIneligibleExternalAccounts = externalAccounts && externalAccounts.length && externalAccounts
            .filter(account => [11, 35, 36].includes(account.registrationType) && account.isSelected)
            .filter(account => {
                if (!account.associatedOverrideAgreementId) {
                    return true;
                }
                const matchingOverride = agreementsGroupByType
                    .filter(agreementGroup => agreementGroup.agreementTypeId === account.registrationType)
                    .filter(agreementGroup => (
                        agreementGroup.agreements.some(agreement => agreement.agreementId === account.associatedOverrideAgreementId)) ||
                        (agreementGroup.existingAgreement && agreementGroup.existingAgreement.agreementId === account.associatedOverrideAgreementId)
                    )
                if (matchingOverride.length) {
                    return false;
                }
                return true;
            }).length;
        const hasBrokerageAdvantageServiceFeature = agreement =>
            agreement.serviceFeatures && agreement.serviceFeatures.some(sf => sf.serviceID === 62);
        // detect ineligible Brokerage and Brokerage Advantage accounts
        const hasIneligibleBrokerage = agreementsGroupByType
            .reduce((agg, agreementGroup) => {
                const productTypeBrokerageAgreements = agreementGroup.agreements
                    .filter(agreement => agreement.productTypeId === 20);
                const invalidBrokerageAdvantage = productTypeBrokerageAgreements
                    .filter(agr => hasBrokerageAdvantageServiceFeature(agr))
                    .filter(agr => agr.holdings.some(hld => hld.ticker.length < 5 && hld.fundName === ""))
                    .length > 0;
                const invalidBrokerage = productTypeBrokerageAgreements
                    .filter(agr => !hasBrokerageAdvantageServiceFeature(agr))
                    .filter(agr => agr.holdings.some(hld => !hld.moneyMarket))
                    .length > 0;
                return agg || invalidBrokerage || invalidBrokerageAdvantage;
            }, false);
        const noPartnerActivateNeeded = status &&
            !status[`${partner}RequiresRASU`] &&
            (!status.modelChange || !status[`${partner}HasRAS`]) &&
            !status[`${partner}HasInternalAccounts`] &&
            !status[`${partner}HasExternalAccounts`];

        // RAS Onboarding IP experience
        if (!previousModelAssetAllocationAssociatedFund) {
            return <OnboardingHub
                clientDetails={clientDetails}
                agreementsGroupByType={agreementsGroupByType}
                externalAccounts={externalAccounts}
                modelAssetAllocationAssociatedFund={modelAssetAllocationAssociatedFund}
                assetsAsOfDate={assetsAsOfDate}
                monthlyFee={monthlyFee}
                isInternal={isInternal}
                hasAccounts={hasAccounts}
                urlParam={urlParam}
                dedicatedTotal={dedicatedTotal}
                hasIneligibleExternalAccounts={hasIneligibleExternalAccounts}
                hasIneligibleBrokerage={hasIneligibleBrokerage}
            />
        }
        // RAS Model Change Only - Return Journey IP experience
        if (!(hasNewNonRasAgreements || externalAccounts.length) && previousModelAssetAllocationAssociatedFund) {
            return <ModelChangeHub
                agreementsGroupByType={agreementsGroupByType}
                modelAssetAllocationAssociatedFund={modelAssetAllocationAssociatedFund}
                previousModelAssetAllocationAssociatedFund={previousModelAssetAllocationAssociatedFund}
                hasAccounts={hasRAS}
                assetsAsOfDate={assetsAsOfDate}
                isInternal={isInternal}
                monthlyFee={monthlyFee}
                dedicatedTotal={dedicatedTotal}
            />
        }
        // RAS Add Account (may include Model Change) - Return Journey IP experience
        return <AccountChangeHub
            clientDetails={clientDetails}
            agreementsGroupByType={agreementsGroupByType}
            externalAccounts={externalAccounts}
            modelAssetAllocationAssociatedFund={modelAssetAllocationAssociatedFund}
            previousModelAssetAllocationAssociatedFund={previousModelAssetAllocationAssociatedFund}
            hasAccounts={hasAccounts || (status.modelChange && hasRAS)}
            modelChange={status.modelChange}
            requiresRASU={status[`${currentUser}RequiresRASU`]}
            noPartnerActivateNeeded={noPartnerActivateNeeded}
            assetsAsOfDate={assetsAsOfDate}
            isInternal={isInternal}
            monthlyFee={monthlyFee}
            dedicatedTotal={dedicatedTotal}
            hasInternalAccounts={status[`${currentUser}HasInternalAccounts`]}
            hasExternalAccounts={status[`${currentUser}HasExternalAccounts`]}
            hasIneligibleExternalAccounts={hasIneligibleExternalAccounts}
            hasIneligibleBrokerage={hasIneligibleBrokerage}
        />
    }

    return (
        clientDetails.length > 0 ? (<>
            <Dialog
                open={showPOAModal}
                onClose={() => dismissPOA()}
                fullWidth
                maxWidth='md'
                id='poa-pu'
                data-metrics-location='PU'
                data-html2canvas-ignore='true'
            >
                <div className='poaModal'>
                <DialogTitle
                    onClose={() => dismissPOA()}
                    id='poa-pu-img-container'
                >
                    <strong>Important information about your Power of Attorney agreements</strong>
                </DialogTitle>
                <DialogContent>
                    Power of Attorney (POA) agreements for your existing account(s) must be updated to complete your enrollment in Retirement Advisory Service.
                    Please download and complete the POA form at your earliest convenience.
                    <br /><br />
                    Please note that you may elect only a single Agent when you complete the updated POA form.
                    <br /><br />
                    <strong>Return completed form to:</strong>
                    <br /><br />
                    T. Rowe Price<br />
                    P.O. Box 17302<br />
                    Baltimore, MD 21297-1302
                    <br /><br />
                    <div className='poaButtonContainer'>
                        <Button
                            variant='text'
                            color='primary'
                            onClick={() => dismissPOA()}
                            id='poa-pu-btn-close'
                        >
                            Not Now
                        </Button>
                        <Button
                            variant='contained'
                            color='primary'
                            endIcon={<GetAppIcon />}
                            onClick={() => downloadPOA()}
                            id='poa-pu-btn-download'
                        >
                            Download Updated POA Form
                        </Button>
                    </div>
                </DialogContent>
                </div>
            </Dialog>
            <Hub />
        </>) :
        <div className='hubSpinnerOuterContainer'>
            <div className='hubSpinnerContainer'>
                <CircularProgress
                    className='hubSpinner'
                    size={200}
                    thickness={1}
                    alt='loading'
                />
            </div>
        </div>
    )
}

export default InvestmentProposalHub;
